import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './Payments.css';

// ─── Manual payment default form ────────────────────────────────────────────
const MANUAL_DEFAULT = {
  full_name: '', email: '', phone: '',
  package_type: 'Trial Lesson', test_centre: '', transmission: 'Automatic',
  amount_pounds: '', payment_method: 'cash', notes: '',
  student_request_id: null,
};

const PACKAGES = ['Trial Lesson', 'Bronze Package', 'Silver Package', 'Gold Package', 'Other'];
const PAYMENT_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'card',          label: 'Card (not Stripe)' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'other',         label: 'Other' },
];

const Payments = () => {
  const [payments, setPayments]     = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Manual payment modal
  const [showManual, setShowManual]         = useState(false);
  const [manualForm, setManualForm]         = useState(MANUAL_DEFAULT);
  const [manualLoading, setManualLoading]   = useState(false);
  const [manualMsg, setManualMsg]           = useState(null);

  // Pending-request picker
  const [pendingReqs, setPendingReqs]       = useState([]);
  const [reqLoading, setReqLoading]         = useState(false);
  const [selectedReq, setSelectedReq]       = useState(null); // the chosen request object
  const searchTimer                         = useRef(null);

  const setField = (k, v) => setManualForm(f => ({ ...f, [k]: v }));

  // Search for pending requests when email changes
  const searchByEmail = useCallback(async (emailVal) => {
    if (!emailVal || emailVal.trim().length < 3) { setPendingReqs([]); return; }
    setReqLoading(true);
    try {
      const data = await api.get(`/admin/student-requests/pending?email=${encodeURIComponent(emailVal.trim())}`);
      setPendingReqs(data?.requests || []);
    } catch (_) {
      setPendingReqs([]);
    } finally {
      setReqLoading(false);
    }
  }, []);

  const handleEmailChange = (val) => {
    setField('email', val);
    setSelectedReq(null);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchByEmail(val), 500);
  };

  const pickRequest = (req) => {
    setSelectedReq(req);
    setPendingReqs([]);
    setManualForm(f => ({
      ...f,
      full_name:          req.full_name  || f.full_name,
      phone:              req.phone      || f.phone,
      package_type:       req.package_type   || f.package_type,
      test_centre:        req.test_centre    || f.test_centre,
      transmission:       req.transmission   || f.transmission,
      student_request_id: req.id,
    }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setManualLoading(true); setManualMsg(null);
    try {
      const res = await api.post('/admin/payments/manual', manualForm);
      setManualMsg({ type: 'success', text: res.message || 'Payment recorded and emails sent.', bookingRef: res.bookingRef });
      setManualForm(MANUAL_DEFAULT);
      setPendingReqs([]);
      setSelectedReq(null);
      fetchPayments(); // refresh list
    } catch (err) {
      setManualMsg({ type: 'error', text: err.message || 'Failed to record payment.' });
    } finally {
      setManualLoading(false);
    }
  };

  const closeManual = () => {
    setShowManual(false);
    setManualMsg(null);
    setManualForm(MANUAL_DEFAULT);
    setPendingReqs([]);
    setSelectedReq(null);
    clearTimeout(searchTimer.current);
  };


  useEffect(() => { fetchPayments(); }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/payments');
      setPayments(Array.isArray(data?.payments) ? data.payments : []);
      setSummary(data?.summary || null);
      setError(null);
    } catch {
      setError('Failed to load payment data.');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fmt  = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtP = (p) => `£${((parseFloat(p) || 0) / 100).toFixed(2)}`;

  // Map package name → canonical upfront fee in pence
  const UPFRONT = { gold: 20000, silver: 15000, bronze: 10000, trial: 7000 };
  const upfrontAmount = (p) => {
    // Refund entries have raw negative amount
    if (p.status === 'refund_out') return p.amount || 0;
    try {
      const meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : (p.metadata || {});
      // If the metadata says it's a refund completion or referral payout, use raw amount
      if (meta.source === 'refund_completed') return p.amount || 0;
      if (meta.type === 'referral_payout') return p.amount || 0;
      const pkg = (meta.package || meta.package_type || meta.pkg_label || '').toLowerCase().replace(/[\s_-]/g, '');
      if (pkg.includes('gold'))   return UPFRONT.gold;
      if (pkg.includes('silver')) return UPFRONT.silver;
      if (pkg.includes('bronze')) return UPFRONT.bronze;
      if (pkg.includes('trial'))  return UPFRONT.trial;
    } catch (_) {}
    return UPFRONT.trial; // default
  };

  const filterByPeriod = (list) => {
    if (periodFilter === 'all') return list;
    const now = new Date();
    const cut = new Date();
    if (periodFilter === 'today') cut.setHours(0, 0, 0, 0);
    if (periodFilter === 'week')  cut.setDate(now.getDate() - 7);
    if (periodFilter === 'month') cut.setDate(now.getDate() - 30);
    return list.filter(p => new Date(p.created_at) >= cut);
  };

  const visible = filterByPeriod(payments).filter(p => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (p.customer_name || '').toLowerCase().includes(q) ||
           (p.customer_email || '').toLowerCase().includes(q);
  });

  const visibleTotal = visible
    .filter(p => p.status === 'succeeded')
    .reduce((s, p) => s + upfrontAmount(p), 0);

  const statusBadge = (s) => {
    const map = { succeeded: 'pm-badge--success', pending: 'pm-badge--warning', refunded: 'pm-badge--info', refund_out: 'pm-badge--danger', failed: 'pm-badge--danger' };
    return `pm-badge ${map[s] || 'pm-badge--secondary'}`;
  };

  if (loading) return (
    <AdminLayout pageTitle="Payments">
      <div className="pm-page">
        <div className="pm-loading">
          <div className="pm-spinner" />
          <p>Loading payment data…</p>
        </div>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout pageTitle="Payments">
      <div className="pm-page">
        {/* Page header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <div>
              <h1 className="pm-title">Payment Transactions</h1>
              <p className="pm-subtitle">All student payments and transaction history</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="pm-refresh-btn"
              onClick={() => { setShowManual(true); setManualMsg(null); }}
              style={{ background: '#1043A0', color: '#fff', border: 'none' }}
            >
              <span style={{ fontSize: 18, lineHeight: 1, marginRight: 4 }}>+</span>
              Manual Payment
            </button>
            <button className="pm-refresh-btn" onClick={fetchPayments}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="pm-alert">{error}</div>}

        {/* Summary KPI cards */}
        <div className="pm-kpis">
          <div className="pm-kpi">
            <div className="pm-kpi-icon pm-kpi-icon--green">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="pm-kpi-body">
              <span className="pm-kpi-label">Total Collected</span>
              <span className="pm-kpi-value">{fmtP(summary?.total_revenue || 0)}</span>
              <span className="pm-kpi-sub">{summary?.successful_payments || 0} successful payments</span>
            </div>
          </div>
          <div className="pm-kpi">
            <div className="pm-kpi-icon pm-kpi-icon--amber">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="pm-kpi-body">
              <span className="pm-kpi-label">Pending</span>
              <span className="pm-kpi-value">{summary?.pending_payments || 0}</span>
              <span className="pm-kpi-sub">{fmtP(summary?.pending_revenue || 0)} awaiting</span>
            </div>
          </div>
          <div className="pm-kpi">
            <div className="pm-kpi-icon pm-kpi-icon--blue">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
            </div>
            <div className="pm-kpi-body">
              <span className="pm-kpi-label">Period Total</span>
              <span className="pm-kpi-value">{fmtP(visibleTotal)}</span>
              <span className="pm-kpi-sub">{visible.filter(p => p.status === 'succeeded').length} in period</span>
            </div>
          </div>
          <div className="pm-kpi">
            <div className="pm-kpi-icon pm-kpi-icon--red">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
              </svg>
            </div>
            <div className="pm-kpi-body">
              <span className="pm-kpi-label">Refunded</span>
              <span className="pm-kpi-value">{fmtP(summary?.total_refunded || 0)}</span>
              <span className="pm-kpi-sub">Total refunds issued</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="pm-controls">
          <div className="pm-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pm-search-input"
            />
          </div>
          <div className="pm-period-tabs">
            {[
              { key: 'all',   label: 'All Time' },
              { key: 'today', label: 'Today' },
              { key: 'week',  label: '7 Days' },
              { key: 'month', label: '30 Days' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPeriodFilter(key)}
                className={`pm-period-tab${periodFilter === key ? ' pm-period-tab--active' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="pm-table-wrap">
          <table className="pm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {visible.length > 0 ? visible.map(p => {
                const isRefundOut = p.status === 'refund_out';
                const meta = (() => { try { return typeof p.metadata === 'string' ? JSON.parse(p.metadata) : (p.metadata || {}); } catch { return {}; } })();
                const isReferralPayout = meta.type === 'referral_payout';
                const amtPence = upfrontAmount(p);
                return (
                  <tr key={`${p.status}-${p.id}`} style={isRefundOut || isReferralPayout ? { background: '#fff5f5' } : {}}>
                    <td className="pm-td-id">
                      {isRefundOut ? (
                        <span style={{ fontSize: '0.7rem', color: '#b91c1c', fontWeight: 700 }}>REFUND</span>
                      ) : isReferralPayout ? (
                        <span style={{ fontSize: '0.7rem', color: '#b91c1c', fontWeight: 700 }}>REFERRAL</span>
                      ) : `#${p.id}`}
                    </td>
                    <td>
                      <span className="pm-customer-name">{p.customer_name || '—'}</span>
                      <span className="pm-customer-email">
                        {p.customer_email || ''}
                        {isRefundOut && p.ref_number && (
                          <span style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                            Ref: {p.ref_number}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="pm-td-amount" style={isRefundOut || isReferralPayout ? { color: '#b91c1c', fontWeight: 700 } : {}}>
                      {(isRefundOut || isReferralPayout) ? `-£${Math.abs(amtPence / 100).toFixed(2)}` : fmtP(amtPence)}
                    </td>
                    <td>
                      <span className={statusBadge(isReferralPayout ? 'refund_out' : p.status)}>
                        {p.status === 'succeeded' && !isReferralPayout ? 'Paid'
                          : p.status === 'refund_out' ? 'Refund Out'
                          : isReferralPayout ? 'Referral Payout'
                          : p.status || 'unknown'}
                      </span>
                    </td>
                    <td className="pm-td-date">{p.created_at ? fmt(p.created_at) : '—'}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="5" className="pm-no-data">
                    {error ? 'Error loading data.' : 'No transactions found for this period.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {visible.length > 0 && (
          <div className="pm-table-footer">
            Showing {visible.length} record{visible.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ─── Manual Payment Modal ──────────────────────────────────────────── */}
      {showManual && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => { if (e.target === e.currentTarget) closeManual(); }}
        >
          <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:580, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Modal header */}
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#0A1F4E' }}>Record Manual Payment</h2>
                <p style={{ margin:'4px 0 0', fontSize:13, color:'#64748b' }}>Cash, bank transfer, or any off-Stripe payment. All booking emails will be sent automatically.</p>
              </div>
              <button onClick={closeManual} style={{ background:'none', border:'none', cursor:'pointer', fontSize:24, color:'#94a3b8', lineHeight:1 }}>&times;</button>
            </div>

            {manualMsg && (
              <div style={{ margin:'16px 24px 0', padding:'12px 16px', borderRadius:8,
                background: manualMsg.type === 'success' ? '#f0fdf4' : '#fff0f0',
                border: `1px solid ${manualMsg.type === 'success' ? '#86efac' : '#fca5a5'}`,
                color: manualMsg.type === 'success' ? '#15803d' : '#b91c1c', fontSize:14 }}>
                {manualMsg.type === 'success' ? '✅ ' : '⚠️ '}{manualMsg.text}
                {manualMsg.bookingRef && (
                  <span style={{ display:'block', marginTop:6, fontFamily:'monospace', fontWeight:700, fontSize:15, letterSpacing:'0.05em' }}>
                    Booking ref: {manualMsg.bookingRef}
                  </span>
                )}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleManualSubmit} style={{ padding:'20px 24px 24px' }}>
              {/* Student details */}
              <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#475569', textTransform:'uppercase', letterSpacing:'0.04em' }}>Student Details</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Full Name *</label>
                  <input required value={manualForm.full_name} onChange={e => setField('full_name', e.target.value)}
                    placeholder="Jane Smith"
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
                </div>
                <div style={{ gridColumn:'1/-1', position:'relative' }}>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Email *</label>
                  <input required type="email" value={manualForm.email}
                    onChange={e => handleEmailChange(e.target.value)}
                    readOnly={!!selectedReq}
                    placeholder="jane@example.com"
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', background: selectedReq ? '#f8fafc' : undefined, color: selectedReq ? '#475569' : undefined }} />
                  {/* Loading spinner */}
                  {reqLoading && (
                    <span style={{ position:'absolute', right:12, top:36, fontSize:12, color:'#94a3b8' }}>Searching…</span>
                  )}
                  {/* Pending request picker */}
                  {pendingReqs.length > 0 && (
                    <div style={{ position:'absolute', zIndex:20, left:0, right:0, top:'100%', background:'#fff', border:'1.5px solid #bae6fd', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', overflow:'hidden', marginTop:4 }}>
                      <div style={{ padding:'8px 12px 6px', fontSize:11, fontWeight:700, color:'#0369a1', background:'#f0f9ff', letterSpacing:'0.04em', textTransform:'uppercase' }}>
                        {pendingReqs.length} pending request{pendingReqs.length !== 1 ? 's' : ''} — click to auto-fill
                      </div>
                      {pendingReqs.map(r => (
                        <button key={r.id} type="button" onClick={() => pickRequest(r)}
                          style={{ display:'block', width:'100%', textAlign:'left', padding:'9px 12px', border:'none', borderTop:'1px solid #f1f5f9', background:'#fff', cursor:'pointer', fontSize:13 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <span style={{ fontWeight:600, color:'#1e293b' }}>{r.full_name || r.email}</span>
                          {r.package_type && <span style={{ marginLeft:8, fontSize:11, color:'#64748b', background:'#f1f5f9', padding:'2px 6px', borderRadius:4 }}>{r.package_type}</span>}
                          {r.test_centre && <span style={{ marginLeft:6, fontSize:11, color:'#94a3b8' }}>{r.test_centre}</span>}
                          <span style={{ float:'right', fontSize:11, color:'#94a3b8' }}>{r.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Selected request banner */}
                {selectedReq && (
                  <div style={{ gridColumn:'1/-1', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:'8px 12px', fontSize:13, color:'#15803d', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span>✅ Auto-filled from request #{selectedReq.id} — <strong>{selectedReq.full_name}</strong></span>
                    <button type="button" onClick={() => { setSelectedReq(null); setField('student_request_id', null); }}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:16, lineHeight:1 }}>×</button>
                  </div>
                )}
                <div>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Phone</label>
                  <input value={manualForm.phone} onChange={e => setField('phone', e.target.value)}
                    placeholder="07XXXXXXXXX"
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
                </div>
              </div>

              {/* Booking details */}
              <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#475569', textTransform:'uppercase', letterSpacing:'0.04em' }}>Booking Details</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                <div>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Package</label>
                  {selectedReq ? (
                    <input type="text" value={manualForm.package_type} readOnly
                      style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#f8fafc', color:'#475569', outline:'none' }} />
                  ) : (
                    <select value={manualForm.package_type} onChange={e => setField('package_type', e.target.value)}
                      style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff' }}>
                      {PACKAGES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Transmission</label>
                  <select value={manualForm.transmission} onChange={e => setField('transmission', e.target.value)}
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff' }}>
                    <option>Automatic</option>
                    <option>Manual</option>
                  </select>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Test Centre</label>
                  <input value={manualForm.test_centre} onChange={e => setField('test_centre', e.target.value)}
                    placeholder="e.g. Waltham Cross"
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
                </div>
              </div>

              {/* Payment details */}
              <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#475569', textTransform:'uppercase', letterSpacing:'0.04em' }}>Payment Details</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                <div>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Amount (£) *</label>
                  <input required type="number" min="1" step="0.01" value={manualForm.amount_pounds}
                    onChange={e => setField('amount_pounds', e.target.value)}
                    placeholder="70.00"
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Payment Method</label>
                  <select value={manualForm.payment_method} onChange={e => setField('payment_method', e.target.value)}
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', background:'#fff' }}>
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ display:'block', fontWeight:600, fontSize:13, marginBottom:4 }}>Internal Notes</label>
                  <textarea value={manualForm.notes} onChange={e => setField('notes', e.target.value)}
                    placeholder="Any additional notes for internal records..."
                    rows={3}
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
                </div>
              </div>

              {/* Email notice */}
              <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'11px 14px', marginBottom:20, fontSize:13, color:'#0369a1' }}>
                📧 <strong>3 automated emails will be sent</strong> to the student: a payment receipt, a booking confirmation, and a lesson request acknowledgement.
              </div>

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button type="button" onClick={closeManual}
                  style={{ padding:'10px 20px', border:'1.5px solid #e2e8f0', borderRadius:8, background:'#fff', cursor:'pointer', fontWeight:600, fontSize:14 }}>
                  Cancel
                </button>
                <button type="submit" disabled={manualLoading}
                  style={{ padding:'10px 24px', border:'none', borderRadius:8, background: manualLoading ? '#94a3b8' : '#1043A0',
                    color:'#fff', cursor: manualLoading ? 'default' : 'pointer', fontWeight:700, fontSize:14 }}>
                  {manualLoading ? 'Recording...' : '✔ Record Payment & Send Emails'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Payments;
